import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const KEY_DISMISSED = "ovamx:install_dismissed_at";
const COOLDOWN_DAYS = 7;

export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone) — don't show
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Cooldown check
    try {
      const dismissed = localStorage.getItem(KEY_DISMISSED);
      if (dismissed) {
        const days = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
        if (days < COOLDOWN_DAYS) return;
      }
    } catch {}

    // iOS Safari detection (no beforeinstallprompt support)
    const ua = window.navigator.userAgent || "";
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (ios) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setEvt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(KEY_DISMISSED, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosHint(false);
  };

  const install = async () => {
    if (isIOS) {
      setShowIosHint(true);
      return;
    }
    if (!evt) return;
    evt.prompt();
    const choice = await evt.userChoice;
    if (choice?.outcome === "accepted") {
      setVisible(false);
    }
    setEvt(null);
  };

  if (!visible) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60]">
      <div className="border hairline bg-cream-card rounded-2xl shadow-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="w-10 h-10 rounded-xl bg-coral/15 text-coral flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base tracking-tight">Instala Ovamx</p>
          <p className="text-[12px] text-ink-mid leading-snug mt-0.5">
            {isIOS
              ? "Toca Compartir y luego «Añadir a pantalla de inicio» para abrir Ovamx como app."
              : "Añade Ovamx a tu pantalla de inicio para abrirla como app, sin barra de navegador."}
          </p>
          {showIosHint && isIOS && (
            <div className="mt-2 text-[11px] bg-cream rounded-lg p-2 border hairline" data-testid="install-prompt-ios-hint">
              <p className="font-mono text-coral mb-1">Pasos en iPhone / iPad:</p>
              <ol className="list-decimal list-inside text-ink-mid space-y-0.5">
                <li>Toca el ícono de Compartir <span className="font-mono">⎙</span></li>
                <li>«Añadir a pantalla de inicio»</li>
                <li>Confirma con «Añadir»</li>
              </ol>
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <button
              onClick={install}
              data-testid="install-prompt-btn"
              className="inline-flex items-center gap-1.5 bg-ink text-cream rounded-full px-3 py-1.5 text-xs hover:bg-coral transition"
            >
              <Download className="w-3.5 h-3.5" />
              {isIOS ? "Cómo instalar" : "Instalar"}
            </button>
            <button
              onClick={dismiss}
              data-testid="install-prompt-dismiss"
              className="text-xs text-ink-mid hover:text-ink px-2"
            >
              Más tarde
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Cerrar"
          data-testid="install-prompt-close"
          className="w-7 h-7 rounded-full hover:bg-cream-deep flex items-center justify-center shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
